import { Fragment, useState, useEffect } from "react";
import { CalendarDays, FileText, Image as ImageIcon, SlidersHorizontal, Type, Users } from "lucide-react";
import { Label } from "@/components/ui/label";
import { FormField as Field } from "@/components/ui/form-field";
import { cn } from "@/components/utils/utils";
import { APPEAR_CLASS } from "./utils/appear";
import { Input } from "@/components/ui/input";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { GuestManager } from "@/components/ui/guest-manager";
import { useI18n } from "@/hooks/useI18n";
import { StepProps } from "@/lib/builder/models/builder.model";
import ImageUpload from "../../components/ui/ImageUpload";
import { IGuest } from "../request-trip/models/travel-request.model";
import { uploadItineraryWallpaper } from "@/utils/imageStorage";
import { MediaItem } from "@/types/media.types";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { useBuilderCapabilities } from "./capabilities/BuilderCapabilitiesContext";

const ItineraryStep = ({
  data,
  onDataChange,
  onImmediateDataChange,
  canProceed,
  setCanProceed,
  compact = false,
}: StepProps) => {
  const { t } = useI18n();
  const isReadOnly = !useBuilderCapabilities().canEdit;
  const [localData, setLocalData] = useState(data);
  const [activeTab, setActiveTab] = useState<'details' | 'cover' | 'description'>('details');

  const handleInputChange = (field: keyof typeof localData, value: string | number) => {
    const newData = localData.update({ [field]: value });
    setLocalData(newData);
    onDataChange(newData);
  };

  const handleImageChange = (_url: string | null, media?: MediaItem | null) => {
    // La cover è un MediaItem come gli elementi di `media`: l'URL da solo non basta,
    // perderebbe l'attribuzione Unsplash.
    const newData = localData.update({ image: media ?? null });
    setLocalData(newData);
    if (onImmediateDataChange) {
      onImmediateDataChange(newData);
    } else {
      onDataChange(newData);
    }
  };

  // Guest management functions
  const addGuest = () => {
    const newGuest: IGuest = { id: Date.now(), age: 25 };
    const newGuestsData = [...(localData.guests_data || []), newGuest];
    const newData = localData.update({ guests_data: newGuestsData });
    setLocalData(newData);
    onDataChange(newData);
  };

  const removeGuest = () => {
    if ((localData.guests_data || []).length <= 1) return;

    const newGuestsData = (localData.guests_data || []).slice(0, -1);
    const newData = localData.update({ guests_data: newGuestsData });
    setLocalData(newData);
    onDataChange(newData);
  };

  const updateGuestAge = (guestId: number, newAge: number) => {
    const validAge = Math.max(0, newAge);
    const newGuestsData = (localData.guests_data || []).map(guest =>
      guest.id === guestId ? { ...guest, age: validAge } : guest
    );
    const newData = localData.update({ guests_data: newGuestsData });
    setLocalData(newData);
    onDataChange(newData);
  };

  useEffect(() => {
    // Un template è completo con titolo e durata: le date arriveranno dalla richiesta.
    const canProceedNow = localData.title.trim() !== '' && (
      localData.isTemplate
        ? !!localData.duration_days
        : localData.start_date !== '' && localData.end_date !== ''
    );
    setCanProceed(canProceedNow);
  }, [localData, setCanProceed]);


  /*
    Tre sezioni, non un modulo unico. La descrizione vuole tutta l'altezza della modale,
    e la cover porta con se' ricerca Unsplash e link esterno: in coda agli altri campi
    obbligavano a scorrere ogni volta per arrivare al titolo.
    La navigazione a sinistra e le taglie dei campi sono quelle del modale richiesta
    cliente (label piccola con icona, controlli alti 36px): due modali gemelle si
    leggono allo stesso modo.
  */
  const tabs = [
    { id: 'details' as const, label: String(t('itinerary.tabDetails')), icon: <SlidersHorizontal className="h-4 w-4" />, optional: false },
    { id: 'cover' as const, label: String(t('itinerary.tabCover')), icon: <ImageIcon className="h-4 w-4" />, optional: true },
    { id: 'description' as const, label: String(t('itinerary.tabDescription')), icon: <FileText className="h-4 w-4" />, optional: true },
  ];

  // Riga di separazione davanti al primo tab facoltativo, come nelle modali attività.
  const firstOptional = tabs.findIndex((tab) => tab.optional);
  const dividerAt = firstOptional > 0 ? firstOptional : -1;

  // La descrizione arriva come HTML dall'editor: `<p></p>` a vuoto non e' contenuto.
  const hasDescription = !!localData.description?.replace(/<[^>]*>/g, '').trim();
  const hasCover = !!localData.image;

  const detailsTab = (
    <div className="space-y-5">
      <Field id="title" label={String(t('itinerary.tripTitle'))} icon={<Type className="h-3 w-3" />} required>
        <Input
          id="title"
          value={localData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          placeholder={t('itinerary.tripTitlePlaceholder')}
          disabled={isReadOnly}
          className="h-9"
        />
      </Field>

      {/* Durata: un template si misura in giorni, un itinerario collegato in date */}
      <Field
        id={localData.isTemplate ? 'durationDays' : 'dates'}
        label={String(localData.isTemplate ? t('playground.durationDays') : t('itinerary.dates'))}
        icon={<CalendarDays className="h-3 w-3" />}
        hint={localData.isTemplate ? String(t('playground.durationDaysHelp')) : undefined}
        required
      >
        {localData.isTemplate ? (
          <Input
            id="durationDays"
            type="number"
            min={1}
            max={90}
            value={localData.duration_days ?? 1}
            onChange={(e) => handleInputChange('duration_days', Math.max(1, Number(e.target.value) || 1))}
            disabled={isReadOnly}
            className="h-9 sm:w-28"
          />
        ) : (
          <div className="sm:max-w-md">
            <DateRangePicker
              startDate={localData.start_date}
              endDate={localData.end_date}
              onStartDateChange={(date) => handleInputChange('start_date', date)}
              onEndDateChange={(date) => handleInputChange('end_date', date)}
              startDatePlaceholder={t('common.from')}
              endDatePlaceholder={t('common.to')}
              disabled={isReadOnly}
              compact
            />
          </div>
        )}
      </Field>

      <Field id="guests" label={String(t('itinerary.guestsTitle'))} icon={<Users className="h-3 w-3" />} hideOptional>
        <GuestManager
          totalGuests={localData.total_guests}
          guests={localData.guests_data || []}
          onAddGuest={addGuest}
          onRemoveGuest={removeGuest}
          onUpdateGuestAge={updateGuestAge}
          disabled={isReadOnly}
          compact
        />
      </Field>
    </div>
  );

  const coverTab = (
    <div className="space-y-2">
      <Label className="text-xs flex items-center gap-1 text-gray-700">
        <ImageIcon className="h-3 w-3" />
        {t('itinerary.coverTitle')}
      </Label>
      {/* ImageUpload porta con se' caricamento, link esterno e ricerca Unsplash: qui ha
          lo spazio per mostrarli senza schiacciare il resto del modulo. */}
      <ImageUpload
        image={localData.image}
        onImageChange={handleImageChange}
        maxSizeMB={10}
        disabled={isReadOnly}
        uploadFn={localData.id ? (file) => uploadItineraryWallpaper(localData.id!, file) : undefined}
        heightClass={compact ? 'h-[240px]' : 'h-[350px]'}
      />
    </div>
  );

  const descriptionTab = (
    <div className="h-full flex flex-col space-y-2">
      <Label className="text-xs flex items-center gap-1 text-gray-700">
        <FileText className="h-3 w-3" />
        {t('itinerary.tripDescription')}
      </Label>
      <RichTextEditor
        value={localData.description}
        onChange={(val) => handleInputChange('description', val)}
        placeholder={t('itinerary.tripDescriptionPlaceholder')}
        readOnly={isReadOnly}
        className="flex-1"
      />
    </div>
  );

  const tabContent = {
    details: detailsTab,
    cover: coverTab,
    description: descriptionTab,
  };

  const tabHasContent: Record<typeof tabs[number]['id'], boolean> = {
    details: false,
    cover: hasCover,
    description: hasDescription,
  };

  return (
    <div className="flex flex-col md:flex-row h-[70vh]">
      <nav className="flex md:flex-col gap-1 shrink-0 md:w-44 p-2 md:p-3 border-b md:border-b-0 md:border-r border-gray-200 overflow-x-auto">
        {tabs.map((tab, index) => (
          <Fragment key={tab.id}>
            {index === dividerAt && (
              <span
                className="shrink-0 self-stretch border-l md:border-l-0 md:border-t border-gray-200 md:my-1.5 mx-1.5 md:mx-1"
                aria-hidden
              />
            )}
            <button
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 rounded-md whitespace-nowrap transition-colors text-left',
                tab.optional
                  ? 'px-3 py-1.5 text-xs font-normal'
                  : 'px-3 py-2 text-sm font-medium',
                activeTab === tab.id
                  ? 'bg-gray-900 text-white'
                  : tab.optional
                    ? 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                    : 'text-gray-600 hover:bg-gray-100',
              )}
            >
              {tab.icon}
              <span className="flex-1">{tab.label}</span>
              {tabHasContent[tab.id] && (
                <span
                  className={cn(
                    'h-1.5 w-1.5 rounded-full shrink-0',
                    activeTab === tab.id ? 'bg-white/70' : 'bg-gray-400',
                  )}
                  aria-hidden
                />
              )}
            </button>
          </Fragment>
        ))}
      </nav>

      {/* key sulla tab: il contenuto rientra in dissolvenza a ogni cambio */}
      <div key={activeTab} className={cn('flex-1 overflow-y-auto p-5 md:p-6', APPEAR_CLASS)}>
        {tabContent[activeTab]}
      </div>
    </div>
  );
};

export default ItineraryStep;